/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page component for Bills
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.1.0
 */

import { memo, useCallback, useEffect, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EFastBasketSwitchboxContext } from '@/main/components/Table/constants';
import { getIsContextForBasket } from '@/main/components/Table/utils/tableUtils';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { resetBasketAction, setBasketContext } from '@components/Table/actions/tableActions';
import { makeSelectorInitBasketList } from '@components/Table/components/footerModal/Basket/basketSelectors';
import { IBasketItem } from '@components/Table/components/footerModal/Basket/basketTypes';
import { useTable } from '@components/Table/hooks/useTable';
import { ITableField, ITableReducer } from '@components/Table/reducers/tableReducer';
import { makeSelectorTableParameters } from '@components/Table/selectors/tableSelectors';
import {
    MapTableNameToApiPath,
    MapTableNameToBasketApiPath,
    MapTableNameToListApiPath,
    MapTableNameToProxyApiPath,
} from '@components/Table/types/tableMapTypes';
import {
    EResetBasketType,
    ILoadTableMetaPayload,
    IResetBasketActionPayload,
    ISwitchCheckboxPayload,
    TableNameType,
} from '@components/Table/types/tableTypes';
import {
    BILLS,
    BILLS_ACCOUNT,
    BILLS_ACCOUNT_PROXY,
    BILLS_BASKET,
    BILLS_BASKET_ACCOUNT,
    BILLS_UNPAID,
    BILLS_UNPAID_ACCOUNT,
    BILLS_UNPAID_ACCOUNT_PROXY,
    BILLS_UNPAID_BASKET,
    BILLS_UNPAID_BASKET_ACCOUNT,
    EXPORT_BILLS,
} from '@modules/app/Api';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationCommon, makeSelectTranslationPageBills } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { makeSelectGuardComponents } from '@modules/role';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { setCookie } from '@modules/storage/storageActions';
import { makeSelectCookies, makeSelectIsDensity } from '@modules/storage/storageSelectors';
import { ECookieKey, ICookies, ISetCookiePayload } from '@modules/storage/storageTypes';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormat } from '@utils/stringFormat';

import Title from '../../components/common/Title/Title';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { wizardBillsDocSetParamsAction } from '../WizardBillsDoc/wizardBillsDocActions';
import { initBillsOnboarding } from './billsActions';
import BillsTable from './components/BillsTable/BillsTable';
import BillsTitle from './components/BillsTitle/BillsTitle';
import { BillsField, PageBillsType } from './pageBillsTypes';

export interface IPageBillsProps {
    type: PageBillsType;
}

export interface IMatchProps {
    clientId: string;
}

/**
 * @description Bills page container with table.
 * @class PageBills
 *
 * @see PageAuthLocale component for locale.
 */

export const PageBills = memo(
    (props: IDispatchProps & IConnectedProps & IPageBillsProps & RouteComponentProps<IMatchProps>) => {
        const {
            initializeOnboarding,
            type,
            cookie,
            setCookie,
            resetBasket,
            parameters,
            openModal,
            componentsFeature,
            isDensity,
            tableCheckedItems,
            match,
        } = props;

        const dispatch = useDispatch();

        const pageBillsTranslation = useSelector(makeSelectTranslationPageBills());

        const [disableCheckedAll, setDisableCheckedAll] = useState(false);
        const [disableExport, setDisableExport] = useState(false);

        const {
            basketAllIn,
            count,
            data,
            fields,
            isLoading,
            isTableResizeEnabled,
            selectedFilters,
            sort,

            loadTableList,
            loadTableMeta,
            saveResizedChange,
            sendTableFields: sendTableFieldsAction,
            setTableSort,
            switchCheckbox,
        } = useTable(TableNameType.bills);

        const isFilterSelected = Object.keys(selectedFilters).length !== 0;

        const clientId = match.params.clientId ?? '';

        const isWizardFastBillsDoc = location.pathname === RoutePath.wizardFastBillsDoc;
        const isWizardFastReportDoc = location.pathname === RoutePath.wizardFastReportDoc;
        const isFastWizard = isWizardFastBillsDoc || isWizardFastReportDoc;
        const isContextForBasket: boolean = getIsContextForBasket();
        const context =
            isContextForBasket ? EFastBasketSwitchboxContext.WIZARD_BILLS_DOCS : undefined;

        const dropFilters = (): void => {
            resetBasket({
                resetBasket: false,
                tableName: TableNameType.bills,
                type: EResetBasketType.CLEAR_FILTER,
                context,
            });
        };

        const sendTableFields = useCallback(
            (fields: ITableReducer[TableNameType]['fields']): void => {
                sendTableFieldsAction({
                    fields: fields,
                    tableName: TableNameType.bills,
                });
            },
            [sendTableFieldsAction],
        );

        const toggleCheckbox = (data: ISwitchCheckboxPayload) => {
            switchCheckbox({
                ...data,
            });
        };

        useEffect(() => {
            // триггерим setParams чтобы сделать валидацию кнопки "Далее"
            if (isFastWizard) {
                dispatch(wizardBillsDocSetParamsAction({}));
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        },[tableCheckedItems]);

        useEffect(() => {
            if (isContextForBasket) {
                dispatch(setBasketContext(context));
            } else {
                dispatch(setBasketContext(''));
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        },[]);

        useEffect(() => {
            initializeOnboarding();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            if (type === PageBillsType.BILLS_UNPAID || type === PageBillsType.BILLS_UNPAID_ACCOUNT) {
                MapTableNameToListApiPath.bills = clientId
                    ? stringFormat(BILLS_UNPAID_ACCOUNT, clientId)
                    : BILLS_UNPAID;
                MapTableNameToBasketApiPath.bills = clientId
                    ? stringFormat(BILLS_UNPAID_BASKET_ACCOUNT, clientId)
                    : BILLS_UNPAID_BASKET;
                MapTableNameToApiPath.bills = BILLS_UNPAID;
                MapTableNameToProxyApiPath.bills = clientId
                    ? stringFormat(BILLS_UNPAID_ACCOUNT_PROXY, clientId)
                    : BILLS_UNPAID;
            } else {
                MapTableNameToListApiPath.bills = clientId ? stringFormat(BILLS_ACCOUNT, clientId) : BILLS;
                MapTableNameToBasketApiPath.bills = clientId
                    ? stringFormat(BILLS_BASKET_ACCOUNT, clientId)
                    : BILLS_BASKET;
                MapTableNameToApiPath.bills = BILLS;
                MapTableNameToProxyApiPath.bills = clientId ? stringFormat(BILLS_ACCOUNT_PROXY, clientId) : BILLS;
            }

            let waitResetBasket = false;
            if (cookie.BILL_PAGE !== type) {
                setCookie({
                    key: ECookieKey.BILL_PAGE,
                    value: type,
                });
                resetBasket({
                    id: clientId,
                    resetBasket: true,
                    tableName: TableNameType.bills,
                    type: EResetBasketType.CLEAR_META,
                    context,
                });
                waitResetBasket = true;
            }
            const loadMetaPayload: ILoadTableMetaPayload = {
                tableName: TableNameType.bills,
                waitResetBasket,
            };
            if (clientId) {
                loadMetaPayload.queryParams = { accountId: clientId };
            }
            loadTableMeta(loadMetaPayload);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [clientId, loadTableMeta, cookie.BILL_PAGE, type]);

        useEffect(() => {
            const billsSelectMaxCount = parameters?.billsSelectMaxCount ?? null;
            const billsExportMaxCount = parameters?.billsExportMaxCount ?? null;
            setDisableCheckedAll((billsSelectMaxCount || billsSelectMaxCount === 0) && billsSelectMaxCount < count);
            setDisableExport(billsExportMaxCount !== 0 && billsExportMaxCount < count);
        }, [parameters, count, disableExport]);

        return (
            <PageLocale
                fileNames={[
                    TranslationFileNamesEnum.common,
                    TranslationFileNamesEnum.corporate,
                    TranslationFileNamesEnum.pageBills,
                ]}
            >
                {
                    isWizardFastBillsDoc &&
                    <Typography isDangerousInnerHTML variant="body1">
                        {pageBillsTranslation.title.subTitles.wizardFastBillsDocSubtitle}
                    </Typography>
                }
                {
                    isWizardFastReportDoc &&
                    <Typography isDangerousInnerHTML variant="body1">
                        {pageBillsTranslation.title.subTitles.wizardFastReportDocSubstitle}
                    </Typography>
                }
                <Title pageName={ECommonTranslationPages.bills} />
                <BillsTitle
                    billsExportMaxCount={parameters?.billsExportMaxCount ?? 0}
                    billsType={type}
                    counter={count}
                    disableExport={disableExport}
                    exportPath={
                        clientId
                            ? `${MapTableNameToApiPath.bills}${EXPORT_BILLS}?accountId=${clientId}`
                            : `${MapTableNameToApiPath.bills}${EXPORT_BILLS}`
                    }
                    hasNewBills={parameters?.hasNewBills ?? true}
                    isFastActionsPage={isFastWizard}
                    isFilterSelected={isFilterSelected}
                    openModal={openModal}
                    sendTableFields={sendTableFields}
                    tableFields={fields}
                    onDropFiltersClick={dropFilters}
                />
                <BillsTable
                    basketAllIn={basketAllIn}
                    billsType={type}
                    clientId={clientId}
                    componentsFeature={componentsFeature}
                    counter={count}
                    data={data}
                    disableCheckedAll={disableCheckedAll}
                    fields={fields as Array<ITableField<BillsField>>}
                    isDensity={isDensity}
                    isLoading={isLoading}
                    isTableResizeEnabled={isTableResizeEnabled}
                    loadTableList={loadTableList}
                    saveResizedChange={saveResizedChange}
                    setTableSort={setTableSort}
                    sort={sort}
                    switchCheckbox={toggleCheckbox}
                    tableCheckedItems={tableCheckedItems}
                />
            </PageLocale>
        );
    },
);

interface IConnectedProps {
    componentsFeature: guardComponentType;
    cookie: ICookies;
    isDensity: boolean;
    parameters: ITableReducer[TableNameType]['parameters'];
    tableCheckedItems: IBasketItem[];
    translation: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    cookie: makeSelectCookies(),
    isDensity: makeSelectIsDensity(),
    parameters: makeSelectorTableParameters(),
    tableCheckedItems: makeSelectorInitBasketList(),
    translation: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    resetBasket: (payload: IResetBasketActionPayload) => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
    openModal: (payload: IModalOpenPayload) => void;
    setCookie: (payload: ISetCookiePayload) => void;
    initializeOnboarding: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    resetBasket: (payload: IResetBasketActionPayload) => dispatch(resetBasketAction(payload)),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    setCookie: (payload: ISetCookiePayload) => dispatch(setCookie(payload)),
    initializeOnboarding: () => dispatch(initBillsOnboarding()),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(PageBills);
